/**
 * Centralized course configuration.
 *
 * Every course page reads from this file — title, pricing, curriculum,
 * downloads, brand copy, and future video IDs. Maintainers should only need
 * to edit this file to change the product, not individual components.
 *
 * TODO(assets): replace download `href` values with final hosted asset URLs.
 * TODO(video): set `trailer.videoUrl` once the AI Kenny trailer is recorded.
 * TODO(avatar): user-supplied HeyGen avatar id goes in `trailer.heygenAvatarId`.
 * TODO(billing): wire `checkout` to the existing billing architecture.
 * TODO(entitlement): move progress persistence to user-scoped storage.
 * TODO(analytics): wire beta form submissions to analytics/persistence.
 */

export type PlatformId = "chatgpt" | "claude" | "gemini";

export interface CoursePlan {
  id: string;
  name: string;
  /** Price in whole US dollars. */
  price: number;
  /** Short label shown on the card, e.g. "Launch offer". */
  tag?: string;
  description: string;
  includes: string[];
  recommended?: boolean;
}

export interface CurriculumModule {
  id: string;
  title: string;
  duration: string;
  summary: string;
  lessons: string[];
}

export interface CourseDownload {
  id: string;
  title: string;
  description: string;
  format: string;
  /** TODO(assets): final URL. `null` renders a "coming with your enrollment" state. */
  href: string | null;
}

export interface CoursePlatform {
  id: PlatformId;
  name: string;
  featureName: string;
  bestFor: string;
  notes: string[];
}

export const COURSE_BRAND = {
  instructor: "AI Kenny",
  brand: "GenieBot.pro",
  poweredBy: "Powered by GenieBot.pro",
  accent: "#FFD400",
} as const;

export const COURSE = {
  id: "personal-ai-assistant",
  slug: "/courses/personal-ai-assistant",
  title: "Build Your First Personal AI Assistant in 30 Minutes",
  shortTitle: "Build Your First Personal AI Assistant",
  subheadline:
    "Turn ChatGPT, Claude, or Gemini into a practical assistant that helps you plan, write, organize, summarize, and think through decisions — without coding.",
  level: "Beginner",
  runtime: "45–60 minute learning experience",
  coreBuild: "About 30 minutes of core build time",
  platformsLabel: "ChatGPT, Claude, Gemini",
  metaDescription:
    "A beginner course from AI Kenny: build one practical personal AI assistant in about 30 minutes using ChatGPT, Claude, or Gemini. No coding required.",
} as const;

/** A recorded AI Kenny video stored with the site. */
export interface CourseVideoAsset {
  videoUrl: string | null;
  posterUrl: string | null;
  captionsUrl: string | null;
  silent: boolean;
  durationLabel: string;
  heygenAvatarId: string;
  adminNote: string;
}

/** Main course walkthrough, shown on the sales page and the video page. */
export const COURSE_TRAILER: CourseVideoAsset = {
  videoUrl: "/course/ai-kenny-course-video-v2.mp4",
  posterUrl: "/course/ai-kenny-course-video-v2-poster.jpg",
  captionsUrl: "/course/ai-kenny-course-video-v2.vtt",
  silent: false,
  durationLabel: "1 minute 33 seconds",
  heygenAvatarId: "2bec6f1089b241fb9593e2216d8aa0d4",
  adminNote:
    "Admin: drop a new video URL into COURSE_TRAILER.videoUrl in src/config/course.ts. No other file needs editing.",
};

/** Short FAQ answers video, shown on the course FAQ page. */
export const COURSE_FAQ_VIDEO: CourseVideoAsset = {
  videoUrl: "/course/ai-kenny-faq-video.mp4",
  posterUrl: "/course/ai-kenny-faq-video-poster.jpg",
  captionsUrl: "/course/ai-kenny-faq-video.vtt",
  silent: false,
  durationLabel: "1 minute 22 seconds",
  heygenAvatarId: "2bec6f1089b241fb9593e2216d8aa0d4",
  adminNote:
    "Admin: drop a new FAQ video URL into COURSE_FAQ_VIDEO.videoUrl in src/config/course.ts.",
};

/**
 * Course updates ("What's new") live in the `course_updates` table and are
 * read through `useCourseUpdates`. Publishing one through the
 * `course-update-broadcast` function also emails every subscribed student.
 */

/** Launch offer options — easy to change or retire. */
export const COURSE_PLANS: CoursePlan[] = [
  {
    id: "founding",
    name: "Founding Launch",
    price: 29,
    tag: "Launch offer",
    description: "The early-supporter price while the course is in its first release window.",
    includes: [
      "Full 30-minute build path",
      "All student downloads",
      "7-Day Challenge",
      "Lifetime access to the current version",
    ],
    recommended: true,
  },
  {
    id: "standard",
    name: "Standard",
    price: 49,
    tag: "Launch offer",
    description: "The regular course price after the launch window closes.",
    includes: [
      "Full 30-minute build path",
      "All student downloads",
      "7-Day Challenge",
      "Lifetime access to the current version",
    ],
  },
  {
    id: "bundle",
    name: "Course + 1:1 Call",
    price: 49,
    tag: "Get walked through it",
    description:
      "The full course plus a one-to-one call with AI Kenny to set your assistant up around your own work.",
    includes: [
      "Everything in Standard",
      "A 1:1 consulting call with AI Kenny",
      "Your assistant reviewed against your real tasks",
      "Follow-up notes after the call",
    ],
  },
  {
    id: "student",
    name: "Student / pay-what-you-can",
    price: 15,
    tag: "Reduced rate",
    description:
      "Same course, reduced price. For students and anyone the full price would keep out. No proof required — take it in good faith.",
    includes: [
      "Full 30-minute build path",
      "All student downloads",
      "7-Day Challenge",
      "Lifetime access to the current version",
    ],
  },
  {
    id: "updates",
    name: "Course + Updates",
    price: 79,
    tag: "Launch offer",
    description: "The course plus future revisions as platforms and prompts change.",
    includes: [
      "Everything in Standard",
      "Future course revisions",
      "New prompt library additions",
      "Updated platform setup sheets",
    ],
  },
];

export const COURSE_OUTCOMES = [
  {
    id: "assistant",
    headline: "1 assistant",
    detail: "One assistant you actually keep using, set up on the platform you already have.",
  },
  {
    id: "jobs",
    headline: "5 repeatable jobs",
    detail: "Five jobs your assistant can do the same way every time: plan, write, organize, summarize, decide.",
  },
  {
    id: "checkpoints",
    headline: "3 daily checkpoints",
    detail: "A morning, midday, and end-of-day rhythm so the assistant fits into a normal day.",
  },
  {
    id: "check",
    headline: "CHECK habit",
    detail: "A short verification habit so you know when to trust an answer and when to confirm it.",
  },
  {
    id: "fix",
    headline: "FIX method",
    detail: "A simple three-step way to repair an assistant that drifts off track.",
  },
  {
    id: "challenge",
    headline: "7-Day Challenge",
    detail: "A one-week plan that turns the setup into a habit instead of a one-time experiment.",
  },
];

export const COURSE_AUDIENCE = {
  forYou: [
    "You have used ChatGPT, Claude, or Gemini a few times but never set anything up.",
    "You want help planning your week, writing clearly, and getting organized.",
    "You want a practical result today, not a long technical course.",
    "You are a student, small business owner, manager, freelancer, or busy parent.",
  ],
  notForYou: [
    "You want to build large automated systems that run without you.",
    "You are looking for software engineering, APIs, or code.",
    "You want an accredited certification, CE credit, or employer-recognized credential.",
  ],
};

export const COURSE_CURRICULUM: CurriculumModule[] = [
  {
    id: "quick-win",
    title: "Quick Win: your starter assistant",
    duration: "5 min",
    summary: "Answer five short questions and walk out with a starter assistant profile.",
    lessons: ["Why one assistant beats ten tools", "The five-question intake", "Your starter profile"],
  },
  {
    id: "choose-path",
    title: "Choose your path",
    duration: "5 min",
    summary: "Set up on ChatGPT, Claude, or Gemini using the platform you already have.",
    lessons: ["ChatGPT Project", "Claude Project", "Gemini Gem", "What carries over between them"],
  },
  {
    id: "instructions",
    title: "Write the master instructions",
    duration: "10 min",
    summary: "The instruction block that makes your assistant behave consistently.",
    lessons: ["Role, tone, and limits", "What to always ask before answering", "Saving it in your platform"],
  },
  {
    id: "five-jobs",
    title: "The five repeatable jobs",
    duration: "10 min",
    summary: "Plan, write, organize, summarize, and think through decisions — the same way every time.",
    lessons: ["Job prompts you can reuse", "Turning a job into a checkpoint", "Adjusting for your work"],
  },
  {
    id: "check",
    title: "CHECK: verifying answers",
    duration: "8 min",
    summary: "A short habit for deciding when an answer is good enough to act on.",
    lessons: ["Spotting claims that need a source", "Comparing when the consequence is real", "Keeping the human decision"],
  },
  {
    id: "fix",
    title: "FIX: repairing a drifting assistant",
    duration: "7 min",
    summary: "Three steps to get an assistant back on track without starting over.",
    lessons: ["Find the failure", "Improve one instruction", "eXecute again"],
  },
  {
    id: "challenge",
    title: "The 7-Day Challenge",
    duration: "10 min",
    summary: "One small task per day for a week so the habit sticks.",
    lessons: ["How the week is structured", "Daily reflection", "What to do after day seven"],
  },
];

export const COURSE_DOWNLOADS: CourseDownload[] = [
  { id: "workbook", title: "Course workbook", description: "Fill-in pages that follow every module.", format: "PDF", href: "/course-downloads/geniebot-course-workbook.pdf" },
  { id: "master-instructions", title: "Master instructions template", description: "The instruction block to paste into your assistant.", format: "PDF", href: "/course-downloads/geniebot-master-instructions.pdf" },
  { id: "prompt-library", title: "25-task prompt library", description: "Twenty-five ready prompts for the five repeatable jobs.", format: "PDF", href: "/course-downloads/geniebot-25-task-prompt-library.pdf" },
  { id: "privacy-checklist", title: "Privacy checklist", description: "What to share with an assistant and what to keep out.", format: "PDF", href: "/course-downloads/geniebot-privacy-checklist.pdf" },
  { id: "setup-chatgpt", title: "ChatGPT setup sheet", description: "Step-by-step setup for a ChatGPT Project.", format: "PDF", href: "/course-downloads/geniebot-setup-sheet-chatgpt.pdf" },
  { id: "setup-claude", title: "Claude setup sheet", description: "Step-by-step setup for a Claude Project.", format: "PDF", href: "/course-downloads/geniebot-setup-sheet-claude.pdf" },
  { id: "setup-gemini", title: "Gemini setup sheet", description: "Step-by-step setup for a Gemini Gem.", format: "PDF", href: "/course-downloads/geniebot-setup-sheet-gemini.pdf" },
  { id: "challenge-sheet", title: "7-Day Challenge sheet", description: "One page per day with the task and reflection space.", format: "PDF", href: "/course-downloads/geniebot-7-day-challenge.pdf" },
  { id: "certificate", title: "Certificate of Completion", description: "Print and sign it once you finish the course. Not an accredited credential.", format: "PDF", href: "/course-downloads/geniebot-certificate-of-completion.pdf" },
];

export const COURSE_PLATFORMS: CoursePlatform[] = [
  {
    id: "chatgpt",
    name: "ChatGPT",
    featureName: "ChatGPT Project",
    bestFor: "People who already pay for or use ChatGPT day to day.",
    notes: [
      "Projects hold instructions and files in one place.",
      "You can keep separate chats inside the same project.",
    ],
  },
  {
    id: "claude",
    name: "Claude",
    featureName: "Claude Project",
    bestFor: "People who work with longer documents and written drafts.",
    notes: [
      "Projects keep your instructions attached to every chat.",
      "Useful when you paste a lot of reference text.",
    ],
  },
  {
    id: "gemini",
    name: "Gemini",
    featureName: "Gemini Gem",
    bestFor: "People already inside Google tools.",
    notes: [
      "Gems store a reusable assistant you can name.",
      "Handy if your notes and docs already live in Google.",
    ],
  },
];

export const COURSE_SAFETY = [
  {
    id: "approval",
    title: "Keep a human on consequential decisions",
    detail:
      "Money, legal, medical, employment, and safety choices stay with you. The assistant drafts and suggests; you approve.",
  },
  {
    id: "secrets",
    title: "Never paste secrets",
    detail:
      "No passwords, private keys, authentication codes, or full account numbers. If you would not email it, do not paste it.",
  },
  {
    id: "verify",
    title: "Verify important claims",
    detail:
      "Assistants can state something confidently and still be wrong. Use the CHECK habit before you act on a claim that matters.",
  },
];

export const COURSE_FAQ = [
  {
    q: "How do I actually build the assistant?",
    a: "You answer five short questions about your role, goals, current projects, preferred tone, and recurring tasks. Those answers become a starter profile. You paste that profile into a saved workspace on the platform you already use — a ChatGPT Project, a Claude Project, or a Gemini Gem — and add the master instructions template from your downloads. Then you give it the five repeatable jobs: plan, write, organize, summarize, and think through a decision. That is the whole build, and it is all plain English typing. No coding, no installs, no API keys.",
  },
  {
    q: "What platforms does it run on?",
    a: "It runs inside ChatGPT, Claude, or Gemini — whichever one you already have. The course includes a setup path for each: a ChatGPT Project, a Claude Project, or a Gemini Gem. All three hold saved instructions that apply to every new chat, so your assistant behaves the same way each time. The instructions you write are plain text, so you can move them from one platform to another if you switch. The course does not require GenieBot.pro or any paid tool of ours to work.",
  },
  {
    q: "How does the assistant learn from me?",
    a: "It does not train on you the way people often imagine. It remembers what you deliberately save. Your starter profile and master instructions live in the project or gem, so every chat starts already knowing your role, your priorities, and how you like to be spoken to. You improve it by editing those saved instructions — that is exactly what the FIX method does: find the one thing that went wrong, change one instruction, run it again. Some platforms also offer their own memory settings; the course shows you where those live so you can decide what to turn on. Anything you have not saved into the instructions is not remembered between chats.",
  },
  {
    q: "Do I need to know how to code?",
    a: "No. Everything in this course is typing in plain English inside an app you already use.",
  },
  {
    q: "Do I need a paid AI subscription?",
    a: "Features and limits differ by platform and plan, and they change. The course shows the setup on all three so you can use what you already have.",
  },
  {
    q: "How long does it really take?",
    a: "The core build is about 30 minutes. Working through the full course with the workbook usually takes 45 to 60 minutes.",
  },
  {
    q: "Which platform should I pick?",
    a: "Pick the one you already use. The course covers ChatGPT, Claude, and Gemini, and the instructions carry over between them.",
  },
  {
    q: "Is this a certification?",
    a: "No. This course is not an accredited certification and does not carry CE credit or employer recognition. When you complete it, you receive a Certificate of Completion showing you finished the course.",
  },
  {
    q: "What if my assistant stops behaving?",
    a: "That is what the FIX method is for: find the failure, improve one instruction, run it again.",
  },
];

export const COURSE_INSTRUCTOR = {
  name: "AI Kenny",
  fullName: "Kenneth Gordon",
  role: "AI strategy, prompt engineering, and responsible AI",
  bio: [
    "AI Kenny is Kenneth Gordon, the builder behind GenieBot.pro. He creates practical AI tools and teaches people how to use AI for real work without coding or unnecessary technical overhead.",
    "His work focuses on AI strategy, prompt engineering, and responsible AI. He turns those subjects into plain-language steps that beginners can apply to planning, writing, organizing, summarizing, and decision support.",
    "This course teaches the same foundation he starts with when helping someone new to AI: one useful assistant, a small set of repeatable jobs, and a habit of checking every important output before using it.",
  ],
  socialLinks: [
    {
      id: "x",
      label: "Follow AI Kenny on X",
      handle: "@Ksg93rd",
      href: "https://x.com/ksg93rd",
    },
    {
      id: "instagram",
      label: "Follow AI Kenny on Instagram",
      handle: "@Ksg93rd",
      href: "https://instagram.com/ksg93rd",
    },
  ],
  consultingHref: "/book-consultation",
  pageHref: "/courses/instructor",
};

/** How AI Kenny teaches — shown on the instructor page. */
export const COURSE_INSTRUCTOR_PRINCIPLES = [
  {
    id: "plain-language",
    title: "Plain language first",
    detail:
      "Everything is written at a 10th-grade reading level. If a term needs a definition, you get the definition before you need the term.",
  },
  {
    id: "one-thing",
    title: "One working thing at the end",
    detail:
      "Each session ends with something you can use — not notes to organize later. In this course, that is one assistant doing five jobs you actually repeat.",
  },
  {
    id: "verify",
    title: "Verify before you trust",
    detail:
      "The CHECK habit is taught alongside the build, so you never hand an AI answer to someone else without knowing how to confirm it.",
  },
  {
    id: "no-hype",
    title: "No hype, no fake urgency",
    detail:
      "No invented certifications, no countdown timers, no borrowed testimonials. What the course does and does not include is stated plainly.",
  },
];

/** Meet-the-instructor video, shown on the instructor page. */
export const COURSE_INSTRUCTOR_VIDEO: CourseVideoAsset = {
  videoUrl: "/course/ai-kenny-instructor-intro.mp4",
  posterUrl: "/course/ai-kenny-instructor-intro-poster.jpg",
  captionsUrl: "/course/ai-kenny-instructor-intro.vtt",
  silent: false,
  durationLabel: "57 seconds",
  heygenAvatarId: "2bec6f1089b241fb9593e2216d8aa0d4",
  adminNote:
    "Admin: drop a new instructor video URL into COURSE_INSTRUCTOR_VIDEO.videoUrl in src/config/course.ts.",
};

/** Placeholder checkout state until billing is wired. */
export const COURSE_CHECKOUT = {
  /** Live: payments run through the create-course-checkout edge function. */
  enabled: true,
  pendingMessage:
    "Checkout is temporarily unavailable. Please try again in a few minutes.",
  /** Promotion codes are entered on the secure Stripe checkout page. */
  couponsEnabled: false,
};

export const COURSE_CHALLENGE_DAYS = [
  { day: 1, title: "Plan Your Day", task: "Give your assistant your real task list and ask for the top 3 priorities, realistic sequencing, and any dependencies or blockers.", time: "5–10 min" },
  { day: 2, title: "Write Faster", task: "Draft or improve one real, low-risk email or message with your assistant. Review it yourself before sending.", time: "5–10 min" },
  { day: 3, title: "Turn Notes Into Action", task: "Paste non-sensitive meeting or personal notes and ask your assistant to extract decisions, action items, owners, deadlines, and unanswered questions. Watch for invented information.", time: "5–10 min" },
  { day: 4, title: "Clean Up Your Tasks", task: "Take a messy task list and sort it into Now / Next / Later / Waiting. Remove duplicates.", time: "5–10 min" },
  { day: 5, title: "Think Through a Decision", task: "Use your assistant on a real low-to-medium-risk decision. Ask for options, pros, cons, assumptions, missing information, and a recommended next step. You make the final call.", time: "5–10 min" },
  { day: 6, title: "Run CHECK + FIX", task: "Score one assistant response using the 5-Point Assistant Check. Use FIX on the weakest category, then rerun the same task.", time: "10 min" },
  { day: 7, title: "Graduate Your Assistant", task: "Run all five core jobs, complete the scorecard, and save your Weekly Tune-Up prompt so the habit sticks.", time: "10–15 min" },
];

export const ASSISTANT_CHECK_CRITERIA = [
  { id: "accuracy", label: "Accuracy", hint: "Are the facts right, and are shaky parts flagged?" },
  { id: "usefulness", label: "Usefulness", hint: "Could you act on this without rewriting it?" },
  { id: "clarity", label: "Clarity", hint: "Is it easy to read on the first pass?" },
  { id: "instruction", label: "Instruction Following", hint: "Did it do what you actually asked?" },
  { id: "unsupported", label: "Unsupported Claims", hint: "Score high when it avoids claims it cannot back up." },
];

export const ASSISTANT_CHECK_BANDS = [
  { min: 22, max: 25, label: "AI Kenny Approved", detail: "Keep it. Use this as your reference answer." },
  { min: 18, max: 21, label: "Almost Ready", detail: "One instruction tweak away. Use FIX on the weakest criterion." },
  { min: 13, max: 17, label: "Needs Training", detail: "Add examples and tighten the job description." },
  { min: 5, max: 12, label: "Rebuild Instructions", detail: "Start the master instructions again from the template." },
];

export function scoreBand(total: number) {
  return (
    ASSISTANT_CHECK_BANDS.find((b) => total >= b.min && total <= b.max) ??
    ASSISTANT_CHECK_BANDS[ASSISTANT_CHECK_BANDS.length - 1]
  );
}

export const FIX_STEPS = [
  { letter: "F", title: "Find the failure", detail: "Name the one thing that went wrong. Not 'it was bad' — 'it ignored my deadline'." },
  { letter: "I", title: "Improve one instruction", detail: "Change a single line in your master instructions. One change at a time keeps the cause clear." },
  { letter: "X", title: "eXecute again", detail: "Run the same request again and compare. If it is still wrong, repeat with a different single change." },
];

export const CHECK_STEPS = [
  { letter: "C", title: "Confirm the important claim", detail: "Pick the one claim the decision rests on." },
  { letter: "H", title: "Hunt for the source", detail: "Ask where it came from, then go look at the original." },
  { letter: "E", title: "Examine the evidence", detail: "Does the source actually say that, and is it current?" },
  { letter: "C", title: "Compare when consequence matters", detail: "For money, legal, health, or safety, get a second source." },
  { letter: "K", title: "Keep the human decision", detail: "You make the call. The assistant prepares it." },
];

export const QUICK_WIN_QUESTIONS = [
  { id: "role", label: "What is your role or what do you spend most of your time doing?", placeholder: "e.g. small business owner, student, team lead" },
  { id: "goals", label: "What are your top goals right now?", placeholder: "e.g. land three new clients, finish the semester strong" },
  { id: "priorities", label: "What projects or priorities are you currently working on?", placeholder: "e.g. launching a website, planning a move" },
  { id: "communication", label: "How do you prefer your assistant to communicate with you?", placeholder: "e.g. short, friendly, no jargon" },
  { id: "recurring", label: "What recurring tasks do you want AI to help with?", placeholder: "e.g. weekly planning, email drafts, meeting notes" },
];

export interface SyllabusDay {
  day: number;
  title: string;
  time: string;
  focus: string;
  lessons: string[];
}

/**
 * Day-by-day syllabus: the build session itself comes from COURSE_CURRICULUM
 * (about 30 minutes), then the 7-Day Challenge from COURSE_CHALLENGE_DAYS,
 * broken into short lessons. Keep in sync with those two sources.
 */
export const COURSE_SYLLABUS: SyllabusDay[] = [
  {
    day: 1,
    title: "Plan Your Day",
    time: "5–10 min",
    focus: "Let the assistant triage your task list.",
    lessons: [
      "Give your assistant your real task list",
      "Ask for the top 3 priorities and a realistic order",
      "Note any dependencies or blockers it flags",
    ],
  },
  {
    day: 2,
    title: "Write Faster",
    time: "5–10 min",
    focus: "Draft one real, low-risk message.",
    lessons: [
      "Draft or improve one real email or message",
      "Review and edit it yourself before sending",
      "Save whatever you corrected as an instruction",
    ],
  },
  {
    day: 3,
    title: "Turn Notes Into Action",
    time: "5–10 min",
    focus: "Convert notes into decisions and next steps.",
    lessons: [
      "Paste non-sensitive meeting or personal notes",
      "Extract decisions, action items, owners, and deadlines",
      "Watch for invented information before you act",
    ],
  },
  {
    day: 4,
    title: "Clean Up Your Tasks",
    time: "5–10 min",
    focus: "Sort the mess into a list you will actually use.",
    lessons: [
      "Sort a messy task list into Now / Next / Later / Waiting",
      "Remove duplicates",
      "Keep the sorted list where you plan",
    ],
  },
  {
    day: 5,
    title: "Think Through a Decision",
    time: "5–10 min",
    focus: "Use the assistant as a thinking partner.",
    lessons: [
      "Pick one real, low-to-medium-risk decision",
      "Ask for options, pros, cons, assumptions, and missing information",
      "Make the final call yourself",
    ],
  },
  {
    day: 6,
    title: "Run CHECK + FIX",
    time: "10 min",
    focus: "Score and repair one answer.",
    lessons: [
      "Score one response with the 5-Point Assistant Check",
      "Use FIX on the weakest category",
      "Rerun the same task and compare",
    ],
  },
  {
    day: 7,
    title: "Graduate Your Assistant",
    time: "10–15 min",
    focus: "Lock in the habit.",
    lessons: [
      "Run all five core jobs once more",
      "Complete the scorecard",
      "Save your Weekly Tune-Up prompt",
    ],
  },
];
